import {
  Field,
  ImageField,
  LinkField,
  NextImage,
  Text,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import Animation from 'components/Generic/Animation';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import { ComponentProps } from 'lib/component-props';
import { ReactNode, useCallback, useMemo } from 'react';
import styles from './SecondaryHeroBanner.module.scss';
import useHelpers from 'src/hooks/helpers';

import useWindowDimensions from '../../../hooks/useWindowDimensions';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

type Fields = {
  backgroundAnimation?: Field<string>;
  backgroundImage?: ImageField;
  title?: TextField;
  subtitle?: TextField;
  primaryCtaLink?: LinkField;
  secondaryCtaLink?: LinkField;
};

type SecondaryHeroBannerProps = ComponentProps & {
  fields: Fields;
  rendering: { params: { linkType: 'Primary' | 'Secondary' | 'Tertiary' } };
};

const SecondaryHeroBanner = ({ fields, rendering }: SecondaryHeroBannerProps): JSX.Element => {
  const desktopAnimation = fields.backgroundAnimation?.value;
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor: boolean | undefined = sitecoreContext.pageEditing;
  const { anchorId } = useHelpers(rendering);
  const isValueExists = useCallback(
    (field?: LinkField) => {
      return isExperienceEditor || field?.value?.text || field?.value?.href;
    },
    [isExperienceEditor]
  );
  const buttonType = useMemo(() => {
    const { linkType } = rendering.params;
    const buttonTypesMap = {
      Primary: 'primary',
      Secondary: 'secondary',
      Tertiary: 'textLink',
    };
    const isSecondButtonAllowed = isValueExists(fields.secondaryCtaLink);
    return isSecondButtonAllowed ? 'primary' : buttonTypesMap[linkType] || 'textLink';
  }, [fields.secondaryCtaLink, isValueExists, rendering.params]);
  const deviceType: string = useWindowDimensions();
  const isAnimationVisible = desktopAnimation && deviceType === 'desktop';

  const BannerMediaContent = (): ReactNode => {
    if (isAnimationVisible) {
      return (
        <Animation
          loop={false}
          animationData={desktopAnimation}
          layout={{ fit: 'fit-width', align: [1, 1] }}
          className={styles['background-video-wrapper']}
          visible
        />
      );
    } else {
      return (
        <div className={styles['background-image-wrapper']}>
          <NextImage field={fields.backgroundImage} fill />
        </div>
      );
    }
  };

  return (
    <section
      id={anchorId}
      data-e2e-component-uid={rendering.uid}
      className={`${styles['secondary-hero-banner']} position-relative secondary-hero-banner`}
    >
      <BannerMediaContent />

      <div className={`${styles['text-block']} container px-4 pb-1 pb-md-2 pb-lg-3 pb-xl-4`}>
        <div className="row">
          <div
            className={`${styles['inner-wrapper']} background-white px-2 px-sm-4 px-xl-5 pt-4 pt-sm-4 pt-xl-5 pb-4 pb-lg-5`}
          >
            <Text field={fields.title} tag="h1" className="col-lg-10 col-sm-11 col-12" />
            <Text field={fields.subtitle} tag="p" className="mt-2 body-large col-md-11 col-lg-12" />
            <div className="d-flex d-md-block flex-column mt-5">
              {isValueExists(fields.primaryCtaLink) && (
                <AbrdnButton
                  buttonType={buttonType}
                  linkField={fields.primaryCtaLink}
                  className="w-fit-content"
                />
              )}
              {isValueExists(fields.secondaryCtaLink) && (
                <AbrdnButton
                  buttonType="secondary"
                  linkField={fields.secondaryCtaLink}
                  className="ms-0 ms-md-3 mt-3 mt-md-0 w-fit-content"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default withDatasourceCheck()<SecondaryHeroBannerProps>(SecondaryHeroBanner);
